<script setup lang="ts">
import { computed } from "vue";
import { useToast } from "vue-toast-notification";
import ExitIcon from "@/components/shared/icons/ExitIcon.vue";
import EyeIcon from "@/components/shared/icons/EyeIcon.vue";
import ProgressActivityIcon from "@/components/shared/icons/ProgressActivityIcon.vue";
import { useImpersonationSession } from "@/composables/useImpersonationSession";
import { ERROR_TIMEOUT } from "@/constants";

const toast = useToast();

const { profile, isImpersonating, isExiting, exit } = useImpersonationSession();

const studentName = computed<string>(() => {
  if (!profile.value) return "";
  if (profile.value.full_name) return profile.value.full_name;

  const composed = [profile.value.preferred_first_name || profile.value.legal_first_name, profile.value.last_name]
    .filter(Boolean)
    .join(" ");

  return composed || profile.value.email || "Unknown student";
});

const studentEmail = computed<string | undefined>(() =>
  studentName.value === profile.value?.email ? undefined : profile.value?.email,
);

const exitImpersonation = async (): Promise<void> => {
  try {
    await exit();
  } catch (error) {
    toast.open({
      message: `Error while Exiting Impersonation ${error}`,
      type: "error",
      position: "bottom",
      duration: ERROR_TIMEOUT,
    });
  }
};
</script>

<template>
  <div
    v-if="isImpersonating"
    id="impersonation-banner"
    role="status"
    class="inline-flex items-center gap-3 h-9 min-w-0 pl-3.5 pr-2 rounded-full bg-warning-100 border border-warning-200"
  >
    <EyeIcon custom-class="w-4 h-4 shrink-0 text-warning-900" />
    <span class="text-xs font-bold uppercase tracking-wider text-warning-900 whitespace-nowrap">Student View</span>
    <span class="w-px h-5 bg-warning-300 shrink-0" aria-hidden="true" />
    <span class="flex items-baseline gap-2 min-w-0 overflow-hidden">
      <span class="text-xs text-gray-600 whitespace-nowrap shrink-0">Viewing as</span>
      <span class="text-sm font-bold text-indigo-base truncate">{{ studentName }}</span>
      <span v-if="studentEmail" class="text-xs text-gray-600 truncate">{{ studentEmail }}</span>
    </span>
    <button
      type="button"
      :disabled="isExiting"
      class="flex items-center gap-1.5 h-6 px-2.5 shrink-0 rounded bg-teal-900 text-white text-xs font-bold tracking-wide hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
      @click="exitImpersonation"
    >
      <ProgressActivityIcon v-if="isExiting" size="12" fill="currentColor" class="animate-spin" />
      <ExitIcon v-else custom-class="w-3 h-3" />
      {{ isExiting ? "Exiting…" : "Exit" }}
    </button>
  </div>
</template>
